import { Box, CircularProgress, TextField, Typography, IconButton } from "@mui/material";
import React, { useRef, useState } from "react";
import UploadImagesSectionDraggable, { UploadableImage } from "../../components/UtilityComponents/UploadImagesSection/UploadImagesSection";
import GGLoaderButton from "../../components/CommonComponents/GGLoaderButton/GGLoaderButton";
import GGDialogueBox, { dialogueType } from "../../components/UtilityComponents/GGDialogueBox/GGDialogueBox";
import { ErrorLogger } from "../../utils/Logger";
import imageCompression from "browser-image-compression";
import "./AskForRevisionModal.scss";
import { Order } from "../../Models/Order";
import { RevisionAPIHandler } from "../../services/NetworkClient/BackendAPIs/RevisionAPIHandler";
import { SessionManager } from "../../services/SessionManager/SessionManager";
import { Revision } from "../../Models/Revision";
import CustomMessageDeliveryHandler from "../../services/ChatManager/CustomMessageDeliveryHandler";
import { useNavigate } from "react-router-dom";
import { OrderTransaction } from "../../Models/OrderSummary";
import { formatCurrencyIndian } from "../../utils/MoneyConversionUtils";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { ResumableUploadTask } from "../../services/UploadClient/ResumableUploadTask";
import { UploadPurpose } from "../../services/NetworkClient/BackendAPIs/UploadAPIHandler";

interface MainContentProps {
    uploading: boolean;
    uploadProgress: number;
    remarks: string;
    setRemarks: React.Dispatch<React.SetStateAction<string>>;
    revisionImages: UploadableImage[];
    setRevisionImages: React.Dispatch<React.SetStateAction<UploadableImage[]>>;
    setShowAbortDialog: React.Dispatch<React.SetStateAction<boolean>>;
    setShowRevisionConfirmationDialog: React.Dispatch<React.SetStateAction<boolean>>;
}

interface PaymentConfirmationProps {
    revisionTransaction: OrderTransaction;
    paymentInstance: any;
    onBack: () => void;
    onProceedToPay: () => void;
    enteredPromoCode: string;
    setEnteredPromoCode: React.Dispatch<React.SetStateAction<string>>;
    applyPromoCode: () => void;
    applyPromoMessage: string;
}

const MainContent: React.FC<MainContentProps> = ({
    uploading,
    uploadProgress,
    remarks,
    setRemarks,
    revisionImages,
    setRevisionImages,
    setShowAbortDialog,
    setShowRevisionConfirmationDialog,
}) => (
    <>
        {uploading ? (
            <div style={{ margin: "16px 0", display: "flex", flexDirection: "column", alignItems: "center" }}>
                <Typography>Uploading... {uploadProgress}%</Typography>
                <progress value={uploadProgress} max={100} style={{ width: "100%" }} />
                <GGLoaderButton
                    color="error"
                    variant="outlined"
                    style={{ margin: "10px", backgroundColor: "white", color: "red" }}
                    onClick={() => setShowAbortDialog(true)}
                    isLoading={false}
                >
                    Abort Upload
                </GGLoaderButton>
            </div>
        ) : (
            <>
                <Typography className="revision-modal-title">
                    Please mention the changes you wish to make in this revision
                </Typography>
                <TextField
                    id="outlined-multiline-static"
                    required
                    multiline
                    rows={5}
                    className="revision-modal-textfield"
                    value={remarks}
                    onChange={e => setRemarks(e.target.value)}
                    placeholder="Describe the changes you want to request"
                />

                <Typography className="revision-modal-upload-label">
                    Upload any supporting attachments (optional)
                </Typography>

                <UploadImagesSectionDraggable
                    editMode={true}
                    maxLimit={100}
                    images={revisionImages}
                    setImages={setRevisionImages}
                />

                <GGLoaderButton
                    className="submit-revision-btn"
                    onClick={() => setShowRevisionConfirmationDialog(true)}
                    isLoading={false}
                    variant="contained"
                >
                    Ask For Revision
                </GGLoaderButton>
            </>
        )}
    </>
);

const PaymentConfirmation: React.FC<PaymentConfirmationProps> = ({
    revisionTransaction,
    paymentInstance,
    onBack,
    onProceedToPay,
    enteredPromoCode,
    setEnteredPromoCode,
    applyPromoCode,
    applyPromoMessage,
}) => (
    <div className="payment-confirmation-container">
        <div className="payment-confirmation-header">
            <IconButton className="back-button" onClick={onBack}>
                <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" className="payment-confirmation-title">
                Revision Payment Confirmation
            </Typography>
        </div>
        
        <div className="payment-details">
            <div className="price-info-grid">
                <div className="price-info-grid-item-left">
                    <Typography variant='subtitle1'>Revision Charge</Typography>
                </div>
                <div className="price-info-grid-item-right">
                    {formatCurrencyIndian(revisionTransaction.basePrice)}
                </div>
                
                <div className="price-info-grid-item-left">
                    <Typography variant='subtitle1'>
                        Platform Charges ({(revisionTransaction.buyerFinancials.platformFee.percentage) * 100}%)
                    </Typography>
                </div>
                <div className="price-info-grid-item-right">
                    {formatCurrencyIndian(revisionTransaction.buyerFinancials.platformFee.actualFeeCharged)}
                </div>
                
                <div className="price-info-grid-item-left">
                    <Typography variant='subtitle1'>
                        GST @ {(revisionTransaction.buyerFinancials.gstDetails.ratePercentage) * 100}% (on platform charges)
                    </Typography>
                </div>
                <div className="price-info-grid-item-right">
                    {formatCurrencyIndian(revisionTransaction.buyerFinancials.gstDetails.actualGstAmount)}
                </div>

                {revisionTransaction.promoDetails?.promoCode && (
                    <>
                        <div className="price-info-grid-item-left">
                            <Typography variant='subtitle1'>
                                Promo Applied ({revisionTransaction.promoDetails?.promoCode})
                            </Typography>
                        </div>
                        <div className="price-info-grid-item-right promo-code">
                            - {formatCurrencyIndian(revisionTransaction.promoDetails?.actualDiscountValue)}
                        </div>
                    </>
                )}
            </div>

            <hr />

            <div className="price-details-div">
                <div className="price-info-grid">
                    <div className="price-info-grid-item-left">
                        <Typography fontWeight={1000} variant='h6'>Total Amount</Typography>
                    </div>
                    <div className="price-info-grid-item-right">
                        <Typography fontWeight={1000} variant='h6'>
                            {formatCurrencyIndian(revisionTransaction.buyerFinancials.totalAmountPayable)}
                        </Typography>
                    </div>
                </div>
            </div>

            {/* PROMOCODE support for future usecases  */}
            {/* <div className="price-details-div">
                <div className="price-info-grid">
                    <input
                        className="promo-text-input"
                        type="text"
                        placeholder="Enter Promo Code"
                        value={enteredPromoCode}
                        onChange={(event) => {
                            let enteredText = event.target.value.toString();
                            setEnteredPromoCode(enteredText.toUpperCase());
                        }}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter')
                                applyPromoCode();
                        }}
                    />
                    <button className="apply-promo-button" onClick={applyPromoCode}>
                        Apply
                    </button>
                </div>
            </div>

            {applyPromoMessage !== "" && (
                <div className="promo-code-message-div">
                    {applyPromoMessage === "Promo code applied!" && (
                        <CheckCircleOutlineIcon style={{ color: 'green' }} />
                    )}
                    {applyPromoMessage === "Invalid code" && (
                        <CancelOutlinedIcon style={{ color: 'maroon' }} />
                    )}
                    {applyPromoMessage === "Please enter promo code" && (
                        <InfoOutlinedIcon style={{ color: 'lightblue' }} />
                    )}
                    <Typography className="promo-apply-message" variant='subtitle1'>
                        {applyPromoMessage}
                    </Typography>
                </div>
            )} */}

            <div className="payment-buttons">
                <GGLoaderButton
                    className="proceed-payment-btn"
                    onClick={onProceedToPay}
                    isLoading={false}
                    variant="contained"
                >
                    Proceed to Pay
                </GGLoaderButton>
            </div>
        </div>
    </div>
);

const AskForRevisionModal = ({ order, onSuccess }: { order: Order, onSuccess?: () => void }) => {
    const navigate = useNavigate()
    const [remarks, setRemarks] = useState<string>("");
    const [revisionImages, setRevisionImages] = useState<UploadableImage[]>([]);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const [abortController, setAbortController] = useState<AbortController | null>(null);
    const [showAbortDialog, setShowAbortDialog] = useState(false);
    const [showRevisionConfirmationDialog, setShowRevisionConfirmationDialog] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadedAssetIds, setUploadedAssetIds] = useState<string[]>([]);
    const uploadTasks = useRef<ResumableUploadTask[]>([]);
    const [showPaymentConfirmation, setShowPaymentConfirmation] = useState(false);
    const [revisionTransaction, setRevisionTransaction] = useState<OrderTransaction | null>(null);
    const [paymentInstance, setPaymentInstance] = useState<any | null>(null);
    const [enteredPromoCode, setEnteredPromoCode] = useState('');
    const [applyPromoMessage, setApplyPromoMessage] = useState('');
    const [currentRevision, setCurrentRevision] = useState<Revision | null>(null);
    const [currentOrder, setCurrentOrder] = useState<Order | null>(null);

    const loadRazorpayScript = () => {
        return new Promise((resolve) => {
            const script = document.createElement('script');
            script.src = 'https://checkout.razorpay.com/v1/checkout.js';
            script.onload = () => {
                resolve(true);
            };
            script.onerror = () => {
                resolve(false);
            };
            document.body.appendChild(script);
        });
    };

    const handleSubmit = async () => {
        setShowRevisionConfirmationDialog(false);
        if (!remarks.trim()) {
            setError("Request description is required.");
            setShowErrorDialog(true);
            return;
        }

        setUploading(true);
        setUploadProgress(0);

        const abortCtrl = new AbortController();
        setAbortController(abortCtrl);

        try {
            const files: File[] = revisionImages
                .map((uploadableFile) => uploadableFile.file)
                .filter((file): file is File => file !== undefined);

            // Compress and watermark images
            const compressedFiles: File[] = await Promise.all(
                files.map(async (file) => {
                    const options = {
                        maxSizeMB: 1,
                        maxWidthOrHeight: 1920,
                        useWebWorker: true,
                        fileType: "image/jpeg",
                        initialQuality: 0.5,
                    };
                    const compressedFile = await imageCompression(file, options);
                    const jpegFileName = file.name.replace(/\.[^/.]+$/, ".jpg");
                    return new File([compressedFile], jpegFileName, { type: "image/jpeg" });
                })
            );

            const percentages = new Array(compressedFiles.length).fill(0);
            const assets = await Promise.all(compressedFiles.map((file, index) => {
                const task = new ResumableUploadTask(file, {
                    purpose: UploadPurpose.REVISION_REQUEST_ATTACHMENT,
                    resourceId: order.id,
                    onProgress: (progress) => {
                        percentages[index] = progress.percentage;
                        setUploadProgress(percentages.reduce((total, value) => total + value, 0) / percentages.length);
                    },
                });
                uploadTasks.current.push(task);
                return task.start();
            }));
            const assetIds = assets.map((asset) => asset.assetId);
            setUploadedAssetIds(assetIds);

            // Prepare revision data (adjust according to your backend)
            const revisionData = {
                requestDesc: remarks,
                requestAttachmentAssetIds: assetIds,
            };
            setLoading(true)

            const { revision, order: ggOrder, paymentInstance } = await RevisionAPIHandler.shared.askForRevision(revisionData, order.id);
            if (paymentInstance) {
                // Store the revision transaction from the order summary
                const lastRevisionTransaction = ggOrder.orderSummary?.revisionTransactions[ggOrder.orderSummary.revisionTransactions.length - 1];
                if (lastRevisionTransaction) {
                    setRevisionTransaction(lastRevisionTransaction);
                    setPaymentInstance(paymentInstance);
                    setCurrentRevision(revision);
                    setCurrentOrder(ggOrder);
                    setShowPaymentConfirmation(true);
                } else {
                    setError("Unable to process payment. Please try again.");
                    setShowErrorDialog(true);
                }
            } else {
                await performPostRevisionPlacementTasks(revision, order);
            }

            setUploading(false);
            setUploadProgress(0);
            setRemarks("");
            setRevisionImages([]);
            setUploadedAssetIds([]);
            setAbortController(null);

            // Close the modal on success
            if (onSuccess) onSuccess();

        } catch (err: any) {
            setUploading(false);
            setUploadProgress(0);
            setAbortController(null);

            setUploadedAssetIds([]);

            setError(err?.message || "Something went wrong. Please try again.");
            setShowErrorDialog(true);
        } finally {
            setLoading(false)
        }
    };

    const performPostRevisionPlacementTasks = async (revision: Revision, order: Order) => {
        try {
            await sendRevisionRequestedCustomMessage(revision, order);
            navigate("/my-messages/", { state: { channelId: order.metaData.channelId } })
        } catch (error) {
            ErrorLogger.log(error, 'AskForRevisionModal.performPostRevisionPlacementTasks');
        }
    }

    const sendRevisionRequestedCustomMessage = async (revision: Revision, order: Order) => {
        const customMessageDeliveryHandler = new CustomMessageDeliveryHandler(order.metaData.channelId);
        await customMessageDeliveryHandler.sendRevisionRequestCustomMessage(revision.id);
    }

    const initPayment = (data: any, ggOrder: Order, revision: Revision) => {
        let buyer = SessionManager.shared.buyer;
        const options = {
            key: process.env.REACT_APP_RAZORPAY_KEY_ID ?? "rzp_test_0ZCJ4koe7Wewfx",
            amount: data.amount,
            currency: data.currency,
            name: "GenieGig",
            description: "Revision request payment for revision id: " + revision.id + " of order id: " + ggOrder.id,
            order_id: data.id,

            handler: async function (response: any) {
                // Sending the response to backend to verify the payment
                setLoading(true)
                try {
                    const { paymentSuccess, } = await RevisionAPIHandler.shared.verifyPlacedRevision(revision.id, response.razorpay_order_id, response.razorpay_payment_id, response.razorpay_signature);
                    if (paymentSuccess) {
                        await performPostRevisionPlacementTasks(revision, ggOrder);
                    } else {
                        setError("Your payment for this revision failed.");
                        setUploadedAssetIds([]);
                        setShowErrorDialog(true);
                    }
                } catch (error) {
                    setUploadedAssetIds([]);
                    setError("Something went wrong while making the transaction. Any money deducted from your account will get credited back to your account within 3-4 working days.");
                    setShowErrorDialog(true);
                    ErrorLogger.log(error, 'AskForRevisionModal.initPayment.handler');
                } finally {
                    setLoading(false)
                }

            },

            prefill: {
                name: buyer?.fullName,
                email: buyer?.email,
                contact: buyer?.mobileNo,
            },
            notes: {
                address: "E-322, Tagore Garden Extension, New Delhi - 110027",
                order_id: ggOrder.id,
            },
            theme: {
                color: "#000000",
            },
        };

        const razorPayInstance = new (window as any).Razorpay(options);
        razorPayInstance.open();
    };


    const abortUpload = async () => {
        if (abortController) {
            abortController.abort();
        }
        setUploading(false);
        setUploadProgress(0);
        setAbortController(null);
        setShowAbortDialog(false);

        uploadTasks.current.forEach((task) => { task.cancel().catch(() => {}); });
        setUploadedAssetIds([]);
    };

    const PROMOMESSAGES = {
        success: "Promo code applied!",
        failure: "Invalid code",
        info_empty: "Please enter promo code"
    };

    const applyPromoCode = async () => {
        if (enteredPromoCode !== "") {
            try {
                setLoading(true);
                // Re-call the revision API with promo code included in revision data
                const revisionData = {
                    requestDesc: remarks,
                    requestAttachmentAssetIds: uploadedAssetIds,
                    promoCode: enteredPromoCode,
                };
                
                const { revision, order: ggOrder, paymentInstance } = await RevisionAPIHandler.shared.askForRevision(revisionData, order.id);
                
                if (paymentInstance) {
                    const lastRevisionTransaction = ggOrder.orderSummary?.revisionTransactions[ggOrder.orderSummary.revisionTransactions.length - 1];
                    if (lastRevisionTransaction) {
                        setRevisionTransaction(lastRevisionTransaction);
                        setPaymentInstance(paymentInstance);
                        setCurrentRevision(revision);
                        setCurrentOrder(ggOrder);
                        setApplyPromoMessage(PROMOMESSAGES.success);
                    }
                }
            } catch (err: any) {
                setApplyPromoMessage(PROMOMESSAGES.failure);
            } finally {
                setLoading(false);
            }
        } else {
            setApplyPromoMessage(PROMOMESSAGES.info_empty);
        }
    };

    const handleBackFromPayment = () => {
        setShowPaymentConfirmation(false);
        setRevisionTransaction(null);
        setPaymentInstance(null);
        setCurrentRevision(null);
        setCurrentOrder(null);
        setEnteredPromoCode('');
        setApplyPromoMessage('');
        abortUpload();
    };

    const handleProceedToPay = async () => {
        if (paymentInstance && currentRevision && currentOrder) {
            await loadRazorpayScript();
            initPayment(paymentInstance, currentOrder, currentRevision);
        }
    };

    return (
        <div className="ask-for-revision-modal-root">
            <GGDialogueBox
                type={dialogueType.Error}
                isOpen={showErrorDialog}
                message={error || "Something went wrong, please try again"}
                closeHandler={() => setShowErrorDialog(false)}
                successHandler={() => { }}
            />
            <GGDialogueBox
                type={dialogueType.Alert}
                isOpen={showAbortDialog}
                message="Are you sure you want to abort the upload? All progress will be lost."
                closeHandler={() => setShowAbortDialog(false)}
                successHandler={abortUpload}
            />
            <GGDialogueBox
                type={dialogueType.Alert}
                isOpen={showRevisionConfirmationDialog}
                message={order.noOfRevisionsIncluded !== 0 ? `Are you sure you want to place this revision request? You have ${order.noOfRevisionsIncluded} revision requests left for this order.` : `Are you sure you want to place this revision request? You have no included revision requests left for this order so you will be charged ₹${order.revisionCharge! / 100} for this revision.`}
                closeHandler={() => setShowRevisionConfirmationDialog(false)}
                successHandler={handleSubmit}
            />
            {loading ?
                <Box className="center" width="100%" height={"50vh"}>
                    <CircularProgress size={64} thickness={3} sx={{ marginBottom: "80px" }} />
                </Box> :
                showPaymentConfirmation && revisionTransaction ? (
                    <PaymentConfirmation
                        revisionTransaction={revisionTransaction}
                        paymentInstance={paymentInstance}
                        onBack={handleBackFromPayment}
                        onProceedToPay={handleProceedToPay}
                        enteredPromoCode={enteredPromoCode}
                        setEnteredPromoCode={setEnteredPromoCode}
                        applyPromoCode={applyPromoCode}
                        applyPromoMessage={applyPromoMessage}
                    />
                ) : (
                    <MainContent
                        uploading={uploading}
                        uploadProgress={uploadProgress}
                        remarks={remarks}
                        setRemarks={setRemarks}
                        revisionImages={revisionImages}
                        setRevisionImages={setRevisionImages}
                        setShowAbortDialog={setShowAbortDialog}
                        setShowRevisionConfirmationDialog={setShowRevisionConfirmationDialog}
                    />
                )
            }
        </div>
    );
};

export default AskForRevisionModal;